import { useCallback, useEffect, useState } from "react";
import { Cloud, CloudOff, AlertOctagon, Send, RefreshCw, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDateTime } from "../lib/format";

const STATE = {
  connected: { label: "Connesso", cls: "bg-emerald-50 border-emerald-200 text-emerald-800", dot: "bg-emerald-500", Icon: Cloud },
  not_connected: { label: "Non connesso", cls: "bg-slate-50 border-slate-200 text-slate-700", dot: "bg-slate-400", Icon: CloudOff },
  error: { label: "Errore", cls: "bg-red-50 border-red-200 text-red-800", dot: "bg-red-500", Icon: AlertOctagon },
};
const LOG = { sent: ["Inviata", CheckCircle2, "text-emerald-600"], failed: ["Fallita", XCircle, "text-red-600"], skipped: ["Saltata", MinusCircle, "text-slate-400"] };

export default function Microsoft365Card() {
  const [st, setSt] = useState(null);
  const [log, setLog] = useState([]);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const [{ data }, { data: ml }] = await Promise.all([api.get("/integrations/microsoft/status"), api.get("/integrations/microsoft/mail-log", { params: { limit: 8 } }).catch(() => ({ data: [] }))]);
    setSt(data); setLog(ml); if (!to && data.sender) setTo(data.sender);
  }, [to]);
  useEffect(() => { load(); }, [load]);

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/microsoft/test-email", { to });
      data.ok ? toast.success(data.message, { description: `Microsoft Graph HTTP ${data.graph_status}` }) : toast.error("Invio non riuscito", { description: data.message });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!st) return null;
  const s = STATE[st.state] || STATE.not_connected;
  return (
    <div data-testid="microsoft365-card" className={`rounded-lg border p-4 space-y-3 ${s.cls}`}>
      <div className="flex items-start gap-3">
        <s.Icon className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-heading font-semibold text-sm">Microsoft 365 · Microsoft Graph</p>
            <span data-testid="m365-state" className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-white/70 border border-current/20"><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</span>
          </div>
          <p className="text-xs mt-1" data-testid="m365-sender">Casella mittente: <span className="font-mono font-medium">{st.sender || "non configurata"}</span> · Autenticazione app-only (OAuth 2.0 client credentials) · Permesso Mail.Send</p>
          {st.state === "connected" && <p className="text-[11px] mt-1 opacity-80">Token valido fino alle {st.token_expires_at ? fmtDateTime(st.token_expires_at) : "—"} (rinnovo automatico) · Ultima verifica {fmtDateTime(st.last_check)}{st.secret_expires ? ` · Client secret scade il ${st.secret_expires}` : ""}</p>}
          {st.state !== "connected" && st.detail && <p data-testid="m365-detail" className="text-xs mt-1 font-medium">{st.detail}</p>}
          {st.missing_env?.length > 0 && <p className="text-[11px] mt-1 font-mono opacity-80">Mancano: {st.missing_env.join(", ")}</p>}
          {st.counts && (
            <div className="mt-2 grid sm:grid-cols-3 gap-2 text-xs">
              <div data-testid="m365-last-sent" className="rounded-md bg-white/70 border border-white px-2.5 py-1.5"><p className="text-[10px] uppercase tracking-wide text-slate-500">Ultimo invio</p>
                <p className="font-medium text-slate-800">{st.last_sent ? fmtDateTime(st.last_sent.created_at) : "—"}</p>{st.last_sent && <p className="text-slate-500 truncate" title={st.last_sent.subject}>→ {st.last_sent.to} · {st.last_sent.kind}</p>}</div>
              <div data-testid="m365-last-error" className={`rounded-md border px-2.5 py-1.5 ${st.last_error ? "bg-red-50 border-red-200" : "bg-white/70 border-white"}`}><p className="text-[10px] uppercase tracking-wide text-slate-500">Ultimo errore</p>
                <p className={`font-medium truncate ${st.last_error ? "text-red-700" : "text-slate-800"}`} title={st.last_error || ""}>{st.last_error || "Nessuno"}</p>{st.last_failed && st.last_error && <p className="text-slate-500">{fmtDateTime(st.last_failed.created_at)}</p>}</div>
              <div data-testid="m365-counts" className="rounded-md bg-white/70 border border-white px-2.5 py-1.5"><p className="text-[10px] uppercase tracking-wide text-slate-500">Storico invii</p>
                <p className="font-medium text-slate-800">{st.counts.sent} inviate · {st.counts.failed} fallite · {st.counts.skipped} saltate</p></div>
            </div>
          )}
        </div>
        <button data-testid="m365-refresh" onClick={load} className="p-1.5 rounded hover:bg-white/60" title="Ricontrolla"><RefreshCw className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid="m365-test-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="destinatario email di test" className="h-9 w-full sm:w-72 bg-white" />
        <Button data-testid="m365-test-btn" onClick={test} disabled={busy || st.state !== "connected"} className="h-9 bg-navy-900 hover:bg-navy-700 text-white gap-1.5"><Send className="w-4 h-4" /> Invia email di test</Button>
      </div>
      {log.length > 0 && (
        <div className="rounded-md bg-white/70 border border-white divide-y divide-slate-100" data-testid="m365-mail-log">
          {log.map((m) => { const [l, I, c] = LOG[m.status] || LOG.skipped; return (
            <div key={m.id} data-testid={`mail-log-${m.id}`} className="px-3 py-1.5 flex items-center gap-2 text-xs">
              <I className={`w-3.5 h-3.5 ${c}`} /><span className={`font-medium ${c}`}>{l}</span>
              <span className="text-slate-600 truncate flex-1">{m.to} · {m.subject}</span>
              <span className="text-slate-400 shrink-0">{m.kind}{m.attempt > 1 ? ` · tent. ${m.attempt}` : ""}{m.graph_status ? ` · HTTP ${m.graph_status}` : ""} · {fmtDateTime(m.created_at)}</span>
              {m.error && <span className="text-red-600 truncate max-w-[16rem]" title={m.error}>{m.error}</span>}
            </div>
          ); })}
        </div>
      )}
    </div>
  );
}
